import { providers } from "@tripian/core";

const CACHE_TTL_MS = 15 * 60 * 1000;

type OperatingDatesEntry = { disabled: boolean; dates: Set<string> };

let disabledProductIds = new Set<number>();
let operatingDatesByProduct = new Map<number, OperatingDatesEntry>();
let lastFetchedAt = 0;
let inFlight: Promise<void> | null = null;

const parseOperatingDatesCsv = (csv: string): Map<number, OperatingDatesEntry> => {
  const result = new Map<number, OperatingDatesEntry>();
  const lines = csv.split("\n").map(line => line.replace(/\r$/, "").trim()).filter(Boolean);

  lines.forEach(line => {
    const firstComma = line.indexOf(",");
    const secondComma = line.indexOf(",", firstComma + 1);
    if (firstComma === -1 || secondComma === -1) return;

    const productId = Number(line.slice(0, firstComma));
    if (!Number.isFinite(productId)) return; // header row

    const disabled = line.slice(firstComma + 1, secondComma).trim() === "t";
    const datesField = line
      .slice(secondComma + 1)
      .trim()
      .replace(/^"/, "")
      .replace(/"$/, "")
      .replace(/^\{/, "")
      .replace(/\}$/, "");
    const dates = new Set(
      datesField
        .split(",")
        .map(d => d.trim())
        .filter(Boolean)
    );

    result.set(productId, { disabled, dates });
  });

  return result;
};

const fetchLivnInventory = async (): Promise<void> => {
  if (!providers.livn) return;

  const [minDetails, operatingDatesCsv] = await Promise.all([
    providers.livn.productMinDetails(),
    providers.livn.operatingDates(),
  ]);

  const nextDisabled = new Set<number>();
  minDetails.forEach(supplier => {
    supplier.products.forEach(product => {
      if (product.disabled) nextDisabled.add(product.id);
    });
  });

  disabledProductIds = nextDisabled;
  operatingDatesByProduct = parseOperatingDatesCsv(operatingDatesCsv);
  lastFetchedAt = Date.now();
};

/** Populates/refreshes the local Livn inventory cache (disabled products + operating dates) if stale. Safe to call repeatedly — concurrent calls share a single in-flight request. */
export const ensureLivnInventoryCache = (): Promise<void> => {
  const isStale = Date.now() - lastFetchedAt > CACHE_TTL_MS;
  if (!isStale) return Promise.resolve();

  if (!inFlight) {
    inFlight = fetchLivnInventory()
      .catch(error => {
        console.error("Livn inventory cache refresh failed:", error);
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
};

export const isLivnProductDisabled = (productId: number): boolean => disabledProductIds.has(productId);

/** Returns undefined when the product isn't present in the cached calendar yet (cache cold or product outside the 366-day window). */
export const livnProductOperatesOn = (productId: number, date: string): boolean | undefined => {
  const entry = operatingDatesByProduct.get(productId);
  if (!entry) return undefined;
  if (entry.disabled) return false;
  return entry.dates.has(date);
};
